import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { DriverItem } from './InsightsView._mock';
import type { HauserEQScoreDrivers } from '@/services/api/hauser-eq/api';

import {
  InspectionDiffCard,
  isHiddenSubscore,
  RepairHorizon,
  ScoreProcessingBanner,
} from '@/components/dashboard/scoring';
import { MaintenanceReserveCard } from '@/components/home/HomeownerHub';
import {
  ManualPricePromptDialog,
  enterListingPriceSx,
} from '@/components/property/ManualPricePromptDialog';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useTranslationPrefix } from '@/translations/i18n';

/**
 * Aggregate drivers from all subscores, interleaving one from each category
 * so the first N items represent different subscores.
 */
const aggregateDrivers = (
  drivers: HauserEQScoreDrivers | null,
  direction: 'driving_up' | 'driving_down',
  subscoreKeys: string[],
): DriverItem[] => {
  if (!drivers) return [];

  // Collect items per subscore (skip "overall" to avoid duplicates)
  const buckets = subscoreKeys
    .filter((k) => k !== 'overall' && drivers[k])
    .map((k) => drivers[k][direction].map((s) => ({ title: '', description: s })));

  // Interleave: round-robin across buckets
  const result: DriverItem[] = [];
  const maxLen = Math.max(...buckets.map((b) => b.length), 0);
  for (let i = 0; i < maxLen; i++) {
    for (const bucket of buckets) {
      if (i < bucket.length) result.push(bucket[i]);
    }
  }
  return result;
};

// Driver-payload keys are snake_case (backend); the visible set drops
// any subscore we've decided not to surface for launch. Aligned with
// HIDDEN_SUBSCORES via the snake-case → PascalCase map below.
const SNAKE_TO_PASCAL: Record<string, string> = {
  home_health: 'HomeHealth',
  repair_burden: 'RepairBurden',
  location_quality: 'LocationQuality',
  risk_exposure: 'RiskExposure',
};
const visibleSubscoreKeys = (keys: string[]) =>
  keys.filter((k) => !isHiddenSubscore(SNAKE_TO_PASCAL[k]));
const CONDITION_SUBSCORE_KEYS = visibleSubscoreKeys(['home_health', 'repair_burden']);
const CONTEXT_SUBSCORE_KEYS = visibleSubscoreKeys([
  'location_quality',
  'risk_exposure',
]);

export const InsightsView = ({
  renderWithoutData = false,
  addTopSpacing = false,
}: {
  renderWithoutData?: boolean;
  addTopSpacing?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { selectedProperty, propertyIssues } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const queryClient = useQueryClient();
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);

  const {
    conditionScore,
    propertyDetails,
    conditionScoreExplanation,
    contextScoreExplanation,
    conditionScoreDrivers,
    contextScoreDrivers,
    isConditionProcessing,
    isNoListPrice,
    data,
  } = useHauserEQResults(propertyId);
  const { stepProcessing } = useUploaderContext();

  const homeValue = propertyDetails?.price ?? selectedProperty?.property.price ?? 0;

  // Repair-cost-to-list-price ratio. Prefer the backend's authoritative value;
  // fall back to a local calc using the canonical price chain.
  const repairPriceRatio = useMemo<number | null>(() => {
    const backendRatio = data?.condition_kpis?.repair_price_ratio;
    if (typeof backendRatio === 'number') return backendRatio;
    const totalCost = data?.condition_kpis?.total_repair_cost ?? 0;
    if (homeValue <= 0 || totalCost <= 0) return null;
    return (totalCost / homeValue) * 100;
  }, [data?.condition_kpis, homeValue]);

  // Aggregate all subscore drivers, interleaved by category
  const conditionDriversUp = useMemo(
    () => aggregateDrivers(conditionScoreDrivers, 'driving_up', CONDITION_SUBSCORE_KEYS),
    [conditionScoreDrivers],
  );
  const conditionDriversDown = useMemo(
    () => aggregateDrivers(conditionScoreDrivers, 'driving_down', CONDITION_SUBSCORE_KEYS),
    [conditionScoreDrivers],
  );
  const contextDriversUp = useMemo(
    () => aggregateDrivers(contextScoreDrivers, 'driving_up', CONTEXT_SUBSCORE_KEYS),
    [contextScoreDrivers],
  );
  const contextDriversDown = useMemo(
    () => aggregateDrivers(contextScoreDrivers, 'driving_down', CONTEXT_SUBSCORE_KEYS),
    [contextScoreDrivers],
  );

  const hasConditionData = !!(
    conditionScore != null ||
    data?.condition_score != null ||
    data?.condition_kpis ||
    conditionScoreExplanation ||
    conditionDriversUp.length ||
    conditionDriversDown.length
  );
  const hasContextData = !!(
    contextScoreExplanation ||
    contextDriversUp.length ||
    contextDriversDown.length
  );

  const hasMaintenanceData = (propertyIssues ?? []).length > 0;

  const isReportProcessing = isConditionProcessing || (stepProcessing >= 1 && stepProcessing < 5);

  if (isReportProcessing && !hasConditionData && !hasContextData && !hasMaintenanceData) {
    return (
      <Box
        data-testid="insights-view"
        sx={{ pt: 0, pb: isMobile ? '10px' : '20px', px: isMobile ? '10px' : 0 }}>
        <ScoreProcessingBanner minHeight={300} />
      </Box>
    );
  }

  if (isNoListPrice && !hasConditionData && !hasContextData && !hasMaintenanceData) {
    // If Blueprint already has a price saved (e.g. a prior manual override), hauser-eq is
    // lagging on recalculation — show a spinner rather than re-prompting for a price.
    if (selectedProperty?.property.price) {
      return (
        <Box
          data-testid="insights-view"
          sx={{
            pt: 0,
            pb: isMobile ? '10px' : '20px',
            px: isMobile ? '10px' : 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 2,
          }}>
          <span style={{ fontSize: 14, color: 'var(--tx-muted)', textAlign: 'center' }}>
            Recalculating condition score…
          </span>
        </Box>
      );
    }

    return (
      <>
        <Box
          data-testid="insights-view"
          sx={{
            pt: 0,
            pb: isMobile ? '10px' : '20px',
            px: isMobile ? '10px' : 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            gap: 2,
          }}>
          <span style={{ fontSize: 14, color: 'var(--tx-muted)', textAlign: 'center', maxWidth: 300 }}>
            Condition insights require a listing price.
          </span>
          <Button
            variant="contained"
            onClick={() => setPriceDialogOpen(true)}
            sx={enterListingPriceSx}>
            Enter listing price
          </Button>
        </Box>
        {selectedProperty && (
          <ManualPricePromptDialog
            open={priceDialogOpen}
            property={selectedProperty.property}
            onClose={() => setPriceDialogOpen(false)}
            onSaved={() => {
              setPriceDialogOpen(false);
              queryClient.invalidateQueries({ queryKey: ['properties'] });
              queryClient.invalidateQueries({ queryKey: ['hauser-eq-results', propertyId] });
              showAlert(t('price.recalculating'), 'info');
            }}
          />
        )}
      </>
    );
  }

  if (!hasConditionData && !hasContextData && !hasMaintenanceData) {
    if (!renderWithoutData) return null;
  }

  return (
    <Box
      data-testid="insights-view"
      sx={{
        // a phone's frame already leaves the gap under the header
        pt: addTopSpacing && !isMobile ? '20px' : 0,
        pb: isMobile ? '10px' : '20px',
        px: isMobile ? '10px' : 0,
      }}>
      {(() => {
        const issuesForDiff = (propertyIssues ?? []).filter(
          (i) => (i.source === 'seller' || i.source === 'buyer') && i.reportType === 'GHI',
        );
        return issuesForDiff.length > 0 ? (
          // InspectionDiffCard can render nothing; an empty wrapper's margin would collapse up into a doubled gap under the header
          <Box sx={{ mb: isMobile ? '10px' : '20px', '&:empty': { display: 'none' } }}>
            <InspectionDiffCard issues={issuesForDiff} />
          </Box>
        ) : null;
      })()}

      <Box sx={{ mb: isMobile ? '10px' : '20px' }}>
        <RepairHorizon
          issues={propertyIssues ?? []}
          repairPriceRatio={repairPriceRatio}
          conditionScore={conditionScore ?? null}
        />
      </Box>

      <Box sx={{ mb: '24px' }}>
        <MaintenanceReserveCard
          homeValue={homeValue}
          issues={propertyIssues ?? []}
          conditionScore={conditionScore ?? null}
          onPrepareBidRequest={() => navigate('/dashboard/inspection-report')}
        />
      </Box>

      {/* the fine print: faint, on the card edge, set to a reading measure */}
      <Box sx={{ px: isMobile ? 0 : '20px' }}>
        <Box
          component="p"
          className="hx-on-ground"
          sx={{
            m: 0,
            maxWidth: 880,
            color: 'var(--tx-faint)',
            fontSize: '11px',
            fontWeight: 500,
            lineHeight: 1.6,
            fontFamily: "'Manrope', sans-serif",
          }}>
          Repair and maintenance cost estimates are approximations derived from publicly available
          regional pricing data and are intended for planning purposes only. Actual costs will vary
          based on property condition, local labor rates, materials, and contractor availability.
          All estimates should be independently verified with licensed contractors prior to making
          any financial or repair decisions.
        </Box>
      </Box>
    </Box>
  );
};

export default InsightsView;
